import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Edit,
  Users,
  FolderOpen,
  PlayCircle,
  StopCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  Download,
  BookOpen,
  Filter,
  Loader2,
  X,
  RotateCcw,
  Trash2,
  MessageSquare,   // <-- ADD THIS
} from "lucide-react";
import { createPortal } from "react-dom";
import Table from "../../Table/Table";
import Loading from "../../Common/Loading";
import CommonPagination from "../../Common/CommonPagination";
import styles from "./SOPListing.module.css";
import sopService, { normalizeSOP } from "../../../services/sop.service";
import SOPAskModal from "./SOPAskModal";
import apiClient from "../../../utils/apiClient";
import statusService, { Status } from "../../../services/status.service";
import { BillingGuideline, SOP } from "../../../types/sop";
import ConfirmModal from "../../Common/ConfirmModal";
import Toast, { ToastType } from "../../Common/Toast";
import CommonDropdown from "../../Common/CommonDropdown";
import { Tooltip } from "../../Common/Tooltip";
import CommonDatePicker from "../../Common/CommonDatePicker";
import { usePermission } from "../../../context/PermissionContext";
import ClientSelectionModal from "./ClientSelectionModal";
import ProviderSelectionModal from "./ProviderSelectionModal";
import authService from "../../../services/auth.service";
import organisationService from "../../../services/organisation.service";
import userService from "../../../services/user.service";
import clientService from "../../../services/client.service";
import SOPReadOnlyView from "../SOPReadOnlyView/SOPReadOnlyView";
import modulesService, { Module } from "../../../services/modules.service";

const SOPListing: React.FC = () => {
  const currentUser = authService.getUser();
  const navigate = useNavigate();
  const [sops, setSops] = useState<SOP[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [askModalSOP, setAskModalSOP] = useState<SOP | null>(null);

  const [selectedSOP, setSelectedSOP] = useState<SOP | null>(null);
  const cptRules = selectedSOP?.codingRulesCPT ?? [];
  const icdRules = selectedSOP?.codingRulesICD ?? [];
  const [modules, setModules] = useState<Module[]>([]);
  
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});

  const hasFailedSOPs = useMemo(() => {
    return sops.some(
      (sop) =>
        sop.status?.code === "FAILED" || sop.status?.code === "AI_FAILED"
    );
  }, [sops]);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    sopId: string;
    action: "activate" | "deactivate" | "delete";
  }>({ isOpen: false, sopId: "", action: "activate" });
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [activeStatusId, setActiveStatusId] = useState<number | null>(null);
  const [inactiveStatusId, setInactiveStatusId] = useState<number | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalSOPs, setTotalSOPs] = useState(0);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Selection Modal State
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [selectedClientForCreation, setSelectedClientForCreation] = useState<{ id: string, name: string, type: string } | null>(null);

const [permissionsLoaded, setPermissionsLoaded] = useState(false);


useEffect(() => {
  const loadPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.email) return;

      const data = await modulesService.getUserModules(user.email);
      setModules(data);
    } catch (error) {
      console.error("Failed to load permissions:", error);
    } finally {
      setPermissionsLoaded(true);
    }
  };

  loadPermissions();
}, []);
const sopModule = modules.find(m => m.name === "sops");
const canReadSOP =
  permissionsLoaded && (sopModule?.privileges?.includes("READ") ?? false);
const canCreateSOP =
    permissionsLoaded && (sopModule?.privileges?.includes("CREATE") ?? false);
const canUpdateSOP =
  permissionsLoaded && (sopModule?.privileges?.includes("UPDATE") ?? false);
const canShareSOP =
  permissionsLoaded && (sopModule?.privileges?.includes("SHARE") ?? false);
const canExportSOP =
  permissionsLoaded && (sopModule?.privileges?.includes("EXPORT") ?? false);  
  const [stats, setStats] = useState({
    totalSOPs: 0,
    activeSOPs: 0,
    inactiveSOPs: 0,
  });

  const hasRowActions = (row: SOP) => {
  const isExtracting = row.status?.code === "EXTRACTING";

  if (canReadSOP) return true; // includes View + Ask SOP
  if (canExportSOP) return true;
  if (canUpdateSOP) return true;

  if (!isExtracting && (
    row.status?.code === "FAILED" ||
    row.status?.code === "AI_FAILED"
  )) return true;

  if (row.status?.code === "EXTRACTING") return true;

  return false;
};

const showActionsColumn = sops.some(row => hasRowActions(row));
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({
    fromDate: null,
    toDate: null,
    organisationId: [],
    clientId: [],
    createdBy: [],
  });
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({
    fromDate: null,
    toDate: null,
    organisationId: [],
    clientId: [],
    createdBy: [],
  });

  const [organisations, setOrganisations] = useState<any[]>([]);
  const [clientsForFilter, setClientsForFilter] = useState<any[]>([]);
  const [usersForFilter, setUsersForFilter] = useState<any[]>([]);
  const [loadingFilterData, setLoadingFilterData] = useState(false);
  const activeFilterCount = Object.entries(activeFilters).filter(
    ([key, value]) => {
      if (key === "fromDate" || key === "toDate") {
        return value !== null;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== "" && value !== null && value !== undefined;
    }
  ).length;

  const formatLocalDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  const getCategoryLabel = (category: SOP["category"]): string => {
    if (typeof category === "string") return category;
    if (category && typeof category === "object") return category.title ?? "—";
    return "—";
  };

  const isInitialMount = React.useRef(true);
  const lastFetchParams = React.useRef("");

  useEffect(() => {
    loadStatuses();
    loadStats();
  }, []);

  useEffect(() => {
    if (!canReadSOP) return;

    // Stringify dependencies that are objects/arrays to prevent reference-based triggers
    const paramsKey = JSON.stringify({
      currentPage,
      itemsPerPage,
      debouncedSearchTerm,
      statusFilter,
      activeFilters
    });

    if (lastFetchParams.current === paramsKey) {
      return;
    }

    lastFetchParams.current = paramsKey;
    loadSOPs(debouncedSearchTerm, statusFilter);
  }, [
    canReadSOP,
    currentPage,
    itemsPerPage,
    debouncedSearchTerm,
    statusFilter,
    activeFilters,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const loadSOPs = async (search?: string, filter?: string) => {

    try {
      if (!canReadSOP) return;
      setLoading(true);
      let statusCode: "ACTIVE" | "INACTIVE" | undefined;

      if (filter === "active") statusCode = "ACTIVE";
      if (filter === "inactive") statusCode = "INACTIVE";

      const data = await sopService.getSOPs({
        skip: currentPage * itemsPerPage,
        limit: itemsPerPage,
        search: search,
        statusCode: statusCode,
        fromDate: activeFilters.fromDate
          ? formatLocalDate(activeFilters.fromDate)
          : undefined,
        toDate: activeFilters.toDate
          ? formatLocalDate(activeFilters.toDate)
          : undefined,
        organisationId: activeFilters.organisationId?.length > 0 ? activeFilters.organisationId.join(',') : undefined,
        createdBy: activeFilters.createdBy?.length > 0 ? activeFilters.createdBy.join(',') : undefined,
        clientId: activeFilters.clientId?.length > 0 ? activeFilters.clientId.join(',') : undefined,
      });
      setSops(
        data.sops.map((sop: any) => ({
          ...sop,
          statusId: sop.statusId ?? sop.status_id,
          providerInfo: sop.providerInfo ?? sop.provider_info ?? {},
          updatedAt: sop.updatedAt ?? sop.updated_at ?? null,
          category:
            typeof sop.category === "string"
              ? sop.category
              : (sop.category?.title ?? "—"),
        }))
      );

      // console.log("SOPS FROM API →", data.sops);
      // console.log("SOPS AFTER NORMALIZE →", data.sops.map((sop: any) => ({
      //   ...sop,
      //   statusId: sop.statusId ?? sop.status_id,
      // })));
      setTotalSOPs(data.total);
    } catch (error) {
      console.error("Failed to load SOPs:", error);
    } finally {
      setLoading(false);
      setIsInitialLoading(false);
    }
  };
  const loadStats = async () => {
    try {
      const data = await sopService.getSOPStats();
      setStats(data);
    } catch (err) {
      console.error("Failed to load SOP stats", err);
    }
  };

  const loadFilterData = async () => {
    try {
      setLoadingFilterData(true);
      const user = currentUser as any;
      const isAdmin = user?.role?.name === "SUPER_ADMIN";

      const promises: Promise<any>[] = [
        userService.getUsers(1, 1000, undefined, "ACTIVE"),
      ];

      if (isAdmin) {
        promises.push(
          organisationService.getOrganisations(1, 1000, undefined, "ACTIVE")
        );
      }

      const results = await Promise.all(promises);
      setUsersForFilter(results[0].users || []);

      if (isAdmin) {
        setOrganisations(results[1].organisations || []);
      }

      // Improved detection for organization login
      const hasOrgContext =
        user?.organisation_id ||
        (user?.role?.name && user.role.name.includes("ORGANISATION"));

      if (hasOrgContext && !isAdmin) {
        // Explicitly fetch clients for this organisation
        const orgId = user?.organisation_id || user?.id;
        const clientsRes = await clientService.getClients(
          1,
          1000,
          undefined,
          "ACTIVE",
          orgId
        );
        setClientsForFilter(clientsRes.clients || []);
      } else {
        const clientsRes = await clientService.getAllClients();
        setClientsForFilter(clientsRes || []);
      }
    } catch (err) {
      console.error("Failed to load filter data", err);
    } finally {
      setLoadingFilterData(false);
    }
  };

  useEffect(() => {
    if (showFilters) {
      loadFilterData();
    }
  }, [showFilters]);

  //   useEffect(() => {
  //   const hasExtracting = sops.some(s => s.status?.code === "EXTRACTING");

  //   if (!hasExtracting) return;

  //   const interval = setInterval(() => {
  //     loadSOPs(debouncedSearchTerm, statusFilter);
  //   }, 5000);

  //   return () => clearInterval(interval);
  // }, [sops]);
  useEffect(() => {
    const hasExtracting = sops.some(s => s.status?.code === "EXTRACTING");

    // Also poll if any SOP has unprocessed extra documents
    const hasDocsExtracting = sops.some(s =>
      (s.documents ?? []).some(
        (doc: any) => doc.category !== "Source file" && doc.processed === false
      )
    );

    if (!hasExtracting && !hasDocsExtracting) return;

    const interval = setInterval(() => {
      loadSOPs(debouncedSearchTerm, statusFilter);
    }, 5000);

    return () => clearInterval(interval);
  }, [sops]);
  const handleApplyFilters = () => {
    setActiveFilters(filters);
    setCurrentPage(0);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      fromDate: null,
      toDate: null,
      organisationId: [],
      clientId: [],
      createdBy: [],
    };
    setFilters(resetFilters);
    setActiveFilters(resetFilters);
    setCurrentPage(0);
    setShowFilters(false);
  };

  const handleOrganisationChange = async (orgIds: string[]) => {
    setFilters((prev) => ({ ...prev, organisationId: orgIds, clientId: [] }));
    const user = currentUser as any;
    // const isAdmin = user?.role?.name === "SUPER_ADMIN";
    const isAdmin = user?.role?.name === "SUPER_ADMIN";

    if (!orgIds || orgIds.length === 0) {
      // Re-load clients if it's an organization user, otherwise clear
      if (!isAdmin) {
        const orgId = user?.organisation_id || user?.id; // Important
        const clientsRes = await clientService.getClients(
          1,
          1000,
          undefined, "ACTIVE", orgId);
        setClientsForFilter(clientsRes.clients || []);
      } else {
        setClientsForFilter([]);
      }
      return;
    }

    try {
      setLoadingFilterData(true);
      const orgIdsStr = orgIds.join(",");
      const clientsRes = await clientService.getClients(1, 1000, undefined, "ACTIVE", orgIdsStr);
      setClientsForFilter(clientsRes.clients || []);
      // const clientsRes = await clientService.getAllClients();
      // setClientsForFilter(clientsRes || []);
    } catch (err) {
      console.error("Failed to load clients for organisation", err);
    } finally {
      setLoadingFilterData(false);
    }
  };

  const loadStatuses = async () => {
    try {
      const data = await statusService.getStatuses();
      setStatuses(data);
      const active = data.find((s) => s.code === "ACTIVE");
      const inactive = data.find((s) => s.code === "INACTIVE");
      setActiveStatusId(active ? Number(active.id) : null);
      setInactiveStatusId(inactive ? Number(inactive.id) : null);
    } catch (error) {
      console.error("Failed to load statuses:", error);
    }
  };

  const handleToggleStatus = (id: string, currentStatusId?: number) => {
    const isActive = currentStatusId === activeStatusId;

    setConfirmModal({
      isOpen: true,
      sopId: id,
      action: isActive ? "deactivate" : "activate",
    });
  };

  const confirmToggleStatus = async () => {
    try {
      setIsConfirmLoading(true);
      if (confirmModal.action === "delete") {
        await sopService.deleteSOP(confirmModal.sopId);
        await Promise.all([
          loadSOPs(debouncedSearchTerm, statusFilter),
          loadStats(),
        ]);
        setToast({
          message: "SOP deleted successfully",
          type: "success",
        });
      } else {
        const newStatusId =
          confirmModal.action === "activate" ? activeStatusId : inactiveStatusId;
        if (newStatusId) {
          await sopService.toggleSOPStatus(confirmModal.sopId, newStatusId);
          await Promise.all([
            loadSOPs(debouncedSearchTerm, statusFilter),
            loadStats(),
          ]);
          setToast({
            message: `SOP ${confirmModal.action === "activate" ? "activated" : "deactivated"} successfully`,
            type: "success",
          });
        }
      }
      setConfirmModal({ isOpen: false, sopId: "", action: "activate" });
    } catch (error) {
      console.error("Failed to update SOP status:", error);
      setToast({
        message: `Failed to ${confirmModal.action} SOP`,
        type: "error",
      });
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const [reanalysingId, setReanalysingId] = useState<string | null>(null);

  const handleReanalyse = async (sop: SOP) => {
    try {
      setReanalysingId(sop.id);
      await sopService.reanalyseSOP(sop.id);
      setToast({ message: "Reanalysis started successfully", type: "success" });
      loadSOPs(debouncedSearchTerm, statusFilter);
    } catch (error: any) {
      console.error("Failed to reanalyse SOP:", error);
      setToast({
        message: error.response?.data?.detail || "Failed to start reanalysis",
        type: "error"
      });
    } finally {
      setReanalysingId(null);
    }
  };

  const [stoppingId, setStoppingId] = useState<string | null>(null);

  const handleStop = async (sop: SOP) => {
    try {
      setStoppingId(sop.id);
      await sopService.stopSOP(sop.id);
      setToast({ message: "Extraction stopped", type: "success" });
      loadSOPs(debouncedSearchTerm, statusFilter);
    } catch (error: any) {
      console.error("Failed to stop SOP:", error);
      setToast({
        message: error.response?.data?.detail || "Failed to stop extraction",
        type: "error"
      });
    } finally {
      setStoppingId(null);
    }
  };

  const filteredSOPs = useMemo(() => {
    return sops;
  }, [sops]);

  const handleDownloadPDF = async (sop: SOP) => {
    try {
      setDownloadingId(sop.id);
      await sopService.downloadSOPPDF(sop.id, sop.title);
      setToast({ message: "PDF downloaded successfully", type: "success" });
    } catch (error) {
      console.error("Failed to download PDF:", error);
      setToast({ message: "Failed to download PDF", type: "error" });
    } finally {
      setDownloadingId(null);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleViewSOP = async (sop: SOP) => {
    try {
      setSelectedSOP(sop);
      setViewModalOpen(true);
      setLoadingDetails(true);
      setExpandedSections({});
      const fullSOP = await sopService.getSOPById(sop.id);
      setSelectedSOP(normalizeSOP(fullSOP));

      // fullSOP.billingGuidelines = normalizeBillingGuidelines(
      //   fullSOP.billingGuidelines || []
      // );

      // setSelectedSOP(fullSOP);
      //         }
    } catch (error) {
      console.error("Failed to fetch SOP details:", error);
      setToast({ message: "Failed to load SOP details", type: "error" });
      setViewModalOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCreateSOP = () => {
    navigate("/sops/create");
  };

  const handleClientSelect = (client: { id: string, name: string, npi: string, type: string }) => {
    setClientModalOpen(false);
    setSelectedClientForCreation(client);

    // Check type (case insensitive just in case, though usually 'Individual' or 'Organization')
    // Requirement: "If client.type === 'Individual'"
    // Adjusting to match backend/frontend constants if needed. 
    // Assuming 'Individual' is the value.
    if (client.type === 'Individual' || client.type === 'NPA1') {
      // NPA1 is legacy code for Individual, covering both just in case.
      navigate("/sops/create", {
        state: {
          clientId: client.id,
          clientName: client.name
        }
      });
    } else {
      // Open Provider Selection
      setProviderModalOpen(true);
    }
  };

  const handleProviderSelect = (providerIds: string[], providers: any[]) => {
    setProviderModalOpen(false);
    if (selectedClientForCreation) {
      navigate("/sops/create", {
        state: {
          clientId: selectedClientForCreation.id,
          clientName: selectedClientForCreation.name,
          providerIds: providerIds,
          selectedProviders: providers // Optional: pass details for display
        }
      });
    }
  };

  const columns = [
    {
      key: "title",
      header: "Title",
      width: "250px",
      render: (_: string, row: SOP) => row.title,
    },
    {
      key: "client_name",
      header: "Client",
      width: "200px",
      render: (_: string, row: SOP) => row.client_name || "-",
    },
    {
      key: "npi",
      header: "NPI",
      width: "120px",
      // render: (_: any, row: SOP) => row.providerInfo?.billingProviderNPI || "-",
      render: (_: any, row: SOP) => row.client_npi || "-"
    },
    {
      key: "category",
      header: "Category",
      width: "150px",
      render: (_: any, row: SOP) => (
        <span className={styles.badge}>{getCategoryLabel(row.category)}</span>
      ),
    },
    {
      key: "updatedAt",
      header: "Last Updated",
      width: "140px",
      render: (_: any, row: SOP) =>
        row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "-",
    },
    ...(currentUser?.role?.name === 'SUPER_ADMIN' || currentUser?.role?.name === 'ORGANISATION_ADMIN' ? [{
      key: 'created_by_name',
      header: 'Created By',
      width: '150px',
      render: (_: any, row: SOP) => row.created_by_name || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}> {row.organisation_name == null ? "Super Admin" : "Organisation"} </span>
    }] : []),
    ...(currentUser?.role?.name === 'SUPER_ADMIN' ? [{
      key: 'organisation_name',
      header: 'Organisation',
      width: '150px',
      render: (_: any, row: SOP) => row.organisation_name || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>
    }] : []),
    {
      key: "status",
      header: "Status",
      width: "160px",
      render: (_: any, row: SOP) => {
        const code = row.status?.code;

        // Check if any non-source-file document is still unprocessed
        const hasExtractionInProgress = (row.documents ?? []).some(
          (doc: any) => doc.category !== "Source file" && doc.processed === false
        );

        const statusBadge = (() => {
          if (code === "EXTRACTING") {
            return (
              <span className={styles.extractingBadge}>
                <Loader2 size={14} className={styles.animateSpin} />
                Extracting...
              </span>
            );
          }
          if (code === "ACTIVE") {
            return <span className={styles.activeBadge}>Active</span>;
          }
          if (code === "INACTIVE") {
            return <span className={styles.inactiveBadge}>Inactive</span>;
          }
          if (code === "FAILED") {
            return <span className={styles.failedBadge}>Failed</span>;
          }
          return <span>-</span>;
        })();

        return (
          <div style={{ display: "flex", gap: "4px" }}>
            {statusBadge}
            {hasExtractionInProgress && code !== "EXTRACTING" && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "11px",
                fontWeight: 600,
                color: "#92400e",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "10px",
                padding: "2px 7px",
                width: "fit-content",
              }}>
                <Loader2 size={10} className={styles.animateSpin} />
                Docs extracting…
              </span>
            )}
          </div>
        );
      },
    },
...(showActionsColumn ? [
  {
      key: "actions",
      header: "Actions",
      width: hasFailedSOPs ? "210px" : "180px",
      render: (_: any, row: SOP) => {
        const isActive = row.statusId === activeStatusId;
        const isExtracting = row.status?.code === "EXTRACTING";

        const disabledStyle = isExtracting
          ? {
            opacity: 0.5,
            pointerEvents: "none" as const,
            cursor: "not-allowed"
          }
          : {};

        return (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center"
            }}
          >
            {canReadSOP && (
              <Tooltip content="View SOP" preferredPosition="left">
                <span style={{ display: "inline-block", ...disabledStyle }}>
                  <button
                    disabled={isExtracting}
                    className={styles.viewButton}
                    onClick={() => handleViewSOP(row)}
                  >
                    <Eye size={14} />
                  </button>
                </span>
              </Tooltip>
            )}
            {/* {canReadSOP && 
            (
              <Tooltip content="Ask SOP" preferredPosition="left">
                <span style={{ display: "inline-block", ...disabledStyle }}>
                  <button
                    disabled={isExtracting}
                    className={styles.viewButton}          // reuse view button style or add askButton
                    style={{
                      backgroundColor: "#f0f1ff",
                      color: "#4f46e5",
                      border: "1px solid #e0e7ff",
                      padding: "6px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isExtracting ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                    }}
                    onClick={() => setAskModalSOP(row)}
                  >
                    <MessageSquare size={14} />
                  </button>
                </span>
              </Tooltip>
            )
            }
  */}
              {canExportSOP && (    
            <Tooltip content="Download PDF" preferredPosition="left">
              <span style={{ display: "inline-block", ...disabledStyle }}>
                <button
                  disabled={isExtracting}
                  className={styles.downloadButton}
                  onClick={() => handleDownloadPDF(row)}
                >
                  {downloadingId === row.id ? (
                    <Loader2 size={14} className={styles.animateSpin} />
                  ) : (
                    <Download size={14} />
                  )}
                </button>
              </span>
            </Tooltip>
              )}
              {canUpdateSOP && (  

            <Tooltip content="Edit" preferredPosition="left">
              <span style={{ display: "inline-block", ...disabledStyle }}>
                <button
                  disabled={isExtracting}
                  className={styles.editButton}
                  onClick={() => navigate(`/sops/edit/${row.id}`)}
                >
                  <Edit size={14} />
                </button>
              </span>
            </Tooltip>
              )}
              {canUpdateSOP && (
            <Tooltip
              content={isActive ? "Deactivate" : "Activate"}
              preferredPosition="left"
            >
              <span style={{ display: "inline-block", ...disabledStyle }}>
                <button
                  disabled={isExtracting}
                  className={`${styles.statusButton} ${isActive ? styles.active : styles.inactive
                    }`}
                  onClick={() => handleToggleStatus(row.id, row.statusId)}
                >
                  {isActive ? <StopCircle size={14} /> : <PlayCircle size={14} />}
                </button>
              </span>
            </Tooltip>
              )}
            {(row.status?.code === "FAILED" || row.status?.code === "AI_FAILED") && (
              <>
                <Tooltip content="Reanalyse" preferredPosition="left">
                  <span style={{ display: "inline-block" }}>
                    <button
                      className={styles.reanalyseButton}
                      onClick={() => handleReanalyse(row)}
                      disabled={reanalysingId === row.id}
                    >
                      {reanalysingId === row.id ? (
                        <Loader2 size={14} className={styles.animateSpin} />
                      ) : (
                        <RotateCcw size={14} />
                      )}
                    </button>
                  </span>
                </Tooltip>

                <Tooltip content="Remove" preferredPosition="left">
                  <span style={{ display: "inline-block" }}>
                    <button
                      className={styles.deleteButton}
                      onClick={() =>
                        setConfirmModal({
                          isOpen: true,
                          sopId: row.id,
                          action: "delete",
                        })
                      }
                      style={{
                        backgroundColor: "#fff7ed",
                        color: "#ea580c",
                        border: "1px solid #ffedd5",
                        padding: "6px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        marginLeft: "4px"
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </span>
                </Tooltip>
              </>
            )}

            {row.status?.code === "EXTRACTING" && (
              <Tooltip content="Stop Extracting" preferredPosition="left">
                <span style={{ display: "inline-block" }}>
                  <button
                    className={styles.stopButton}
                    onClick={() => handleStop(row)}
                    disabled={stoppingId === row.id}
                    style={{
                      backgroundColor: "#fef2f2",
                      color: "#ef4444",
                      border: "1px solid #fee2e2",
                      padding: "6px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    {stoppingId === row.id ? (
                      <Loader2 size={14} className={styles.animateSpin} />
                    ) : (
                      <X size={14} />
                    )}
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        );
      }
}] : [])
  ];
  return (
    <div className={styles.container}>
      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <div
          className={`${styles.statCard} ${statusFilter === "all" ? styles.statCardActiveBlue : ""}`}
          onClick={() => setStatusFilter("all")}
        >
          <div className={styles.statIcon}>
            <BookOpen size={16} />
          </div>
          <div className={styles.statContent}>
            <h3>{stats.totalSOPs}</h3>
            <p>Total SOPs</p>
          </div>
        </div>
        <div
          className={`${styles.statCard} ${statusFilter === "active" ? styles.statCardActiveGreen : ""}`}
          onClick={() => setStatusFilter("active")}
        >
          <div
            className={styles.statIcon}
            style={{ backgroundColor: "#dcfce7", color: "#166534" }}
          >
            <Users size={16} />
          </div>
          <div className={styles.statContent}>
            <h3>{stats.activeSOPs}</h3>
            <p>Active SOPs</p>
          </div>
        </div>
        <div
          className={`${styles.statCard} ${statusFilter === "inactive" ? styles.statCardActiveRed : ""}`}
          onClick={() => setStatusFilter("inactive")}
        >
          <div
            className={styles.statIcon}
            style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}
          >
            <FolderOpen size={16} />
          </div>
          <div className={styles.statContent}>
            <h3>{stats.inactiveSOPs}</h3>
            <p>Inactive SOPs</p>
          </div>
        </div>
      </div>

      {/* Header with Title and Actions */}
      <div className={styles.header}>
        <h1>
          <BookOpen size={20} style={{ marginRight: "8px" }} />
          Standard Operating Procedures
        </h1>
        <div className={styles.actions}>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} size={16} />
            <input
              type="text"
              placeholder="Search by Provider, Practice, Category, NPI..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <button
            className={`${styles.filterButton} ${activeFilterCount > 0 ? styles.active : ''}`}
            onClick={() => setShowFilters(true)}
          >
            <Filter size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span className={styles.filterBadge}>{activeFilterCount}</span>
            )}
          </button>
          {/* <button
            className={`${styles.createButton} ${!canCreateSOP ? styles.disabled : ""}`}
            onClick={() => canCreateSOP && navigate("/sops/create")}
            title={
              canCreateSOP
                ? "Create new SOP"
                : "You do not have permission to create SOPs"
            }
          >
            <Plus size={14} />
            Create New SOP
          </button> */}
         {canCreateSOP && (
  <button
    className={styles.createButton}
    onClick={handleCreateSOP}
  >
    <Plus size={14} />
    Create New SOP
  </button>
)}
        </div>
      </div>

      {/* Table */}
{!canReadSOP ? (
  <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
    You do not have permission to view SOPs
  </div>
) : isInitialLoading ? (
  <div className={styles.tableSection}>
    <Loading message="Loading SOPs..." />
  </div>
) : (
  <Table
    className={styles.sopTable}
    data={filteredSOPs}
    columns={columns}
    maxHeight="calc(100vh - 240px)"
  />
)}

      {/* View Modal */}
      {viewModalOpen && selectedSOP && (
        <SOPReadOnlyView
          sop={selectedSOP}
          onClose={() => setViewModalOpen(false)}
          isLoading={loadingDetails}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({ isOpen: false, sopId: "", action: "activate" })
        }
        onConfirm={confirmToggleStatus}
        loading={isConfirmLoading}
        title={`${confirmModal.action === "delete"
            ? "Remove"
            : confirmModal.action === "activate"
              ? "Activate"
              : "Deactivate"
          } SOP`}
        message={`Are you sure you want to ${confirmModal.action === "delete" ? "remove" : confirmModal.action
          } this SOP?`}
        confirmText={
          confirmModal.action === "delete"
            ? "Remove"
            : confirmModal.action === "activate"
              ? "Activate"
              : "Deactivate"
        }
        type={
          confirmModal.action === "delete"
            ? "warning"
            : confirmModal.action === "activate"
              ? "info"
              : "warning"
        }
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <CommonPagination
        show={totalSOPs > 0}
        pageCount={Math.ceil(totalSOPs / itemsPerPage)}
        currentPage={currentPage}
        totalItems={totalSOPs}
        itemsPerPage={itemsPerPage}
        onPageChange={(data) => setCurrentPage(data.selected)}
        onItemsPerPageChange={(items) => {
          setItemsPerPage(items);
          setCurrentPage(0);
        }}
      />

      {/* Selection Modals */}
      <ClientSelectionModal
        isOpen={clientModalOpen}
        onClose={() => setClientModalOpen(false)}
        onSelect={handleClientSelect}
      />

      {selectedClientForCreation && (
        <ProviderSelectionModal
          isOpen={providerModalOpen}
          onClose={() => setProviderModalOpen(false)}
          clientId={selectedClientForCreation?.id || ""}
          clientName={selectedClientForCreation?.name || ""}
          onSelect={handleProviderSelect}
        />
      )}
          {askModalSOP && (
        <SOPAskModal
          sop={askModalSOP}
          onClose={() => setAskModalSOP(null)}
        />
      )}
 

      {/* Filter Modal */}
      {showFilters &&
        createPortal(
          <div className={styles.filterOverlay} onClick={() => setShowFilters(false)}>
            <div className={styles.filterModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.filterHeader}>
                <h2>
                  <Filter size={18} />
                  Filters
                </h2>
                <button
                  className={styles.closeFilterBtn}
                  onClick={() => setShowFilters(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className={styles.filterContent}>
                {/* Date Range */}
                <div className={styles.filterGroup}>
                  <label>Date Range</label>
                  <div className={styles.dateRangeInputs}>
                    <CommonDatePicker
                      selected={filters.fromDate}
                      onChange={(date) =>
                        setFilters((prev) => ({ ...prev, fromDate: date }))
                      }
                      placeholderText="From Date"
                    />
                    <CommonDatePicker
                      selected={filters.toDate}
                      onChange={(date) =>
                        setFilters((prev) => ({ ...prev, toDate: date }))
                      }
                      placeholderText="To Date"
                    />
                  </div>
                </div>

                {/* Organisation Filter - Only for Super Admin */}
                {(currentUser as any)?.role?.name === "SUPER_ADMIN" && (
                  <div className={styles.filterGroup}>
                    <label>Organisation</label>
                    <CommonDropdown
                      options={organisations.map((org) => ({
                        value: org.id,
                        label: org.name,
                      }))}
                      value={filters.organisationId}
                      onChange={handleOrganisationChange}
                      placeholder="Select Organisations"
                      loading={loadingFilterData}
                      isMulti={true}
                      isSearchable={true}
                      size="md"
                    />
                  </div>
                )}

                {/* Client Filter */}
                {/* {(currentUser as any)?.role?.name === "SUPER_ADMIN" || (currentUser as any)?.role?.name === "ORGANISATION_ADMIN" ? ( */}
                <div className={styles.filterGroup}>
                  <label>Client</label>
                  <CommonDropdown
                    options={clientsForFilter.map((client) => ({
                      value: client.id,
                      label: client.name || client.business_name || `${client.first_name} ${client.last_name}`,
                      // label: client.business_name || `${client.first_name} ${client.last_name}`,
                    }))}
                    value={filters.clientId}
                    onChange={(val) =>
                      setFilters((prev) => ({ ...prev, clientId: val }))
                    }
                    placeholder="Select Clients"
                    loading={loadingFilterData}
                    isMulti={true}
                    isSearchable={true}
                    disabled={
                      (currentUser as any)?.role?.name === "SUPER_ADMIN" &&
                      (!filters.organisationId || filters.organisationId.length === 0)
                    }
                    size="md"
                  />
                </div>
                {/* // ) : null} */}

                {/* Created By Filter */}
                {(currentUser as any)?.role?.name === "SUPER_ADMIN" || (currentUser as any)?.role?.name === "ORGANISATION_ADMIN" ? (
                  <div className={styles.filterGroup}>
                    <label>Created By</label>
                    <CommonDropdown
                      options={usersForFilter.map((user) => ({
                        value: user.id,
                        label: `${user.first_name} ${user.last_name} (${user.username})`,
                      }))}
                      value={filters.createdBy}
                      onChange={(val) =>
                        setFilters((prev) => ({ ...prev, createdBy: val }))
                      }
                      placeholder="Select Creators"
                      loading={loadingFilterData}
                      isMulti={true}
                      isSearchable={true}
                      size="md"
                    />
                  </div>
                ) : null}
              </div>

              <div className={styles.filterFooter}>
                <button
                  className={styles.resetFilterBtn}
                  onClick={handleResetFilters}
                >
                  Reset All
                </button>
                <button
                  className={styles.applyFilterBtn}
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SOPListing;
